import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { isPayoutMethodConnected } from '../../util/userHelpers';
import { richText } from '../../util/richText';
import PipAwareVideo from '../../components/PipAwareVideo/PipAwareVideo';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchProgramListings,
} from './ListingPage.duck';

import {
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ListingPageCarouselSkeleton from './ListingPageCarouselSkeleton';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import { ReactComponent as BenefitIcon } from '../../assets/svg/benefitIcon.svg';
import { ReactComponent as OneOnOneIcon } from '../../assets/svg/oneOnOneIcon.svg';
import { ReactComponent as WorkshopIcon } from '../../assets/svg/workshopIcon.svg';
import { ReactComponent as PdfIcon } from '../../assets/svg/pdfIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';
import { CiGlobe } from 'react-icons/ci';

import css from './ListingPage.module.css';
import { ExpandableBio } from './UserCard/UserCard.js';
import GuestCountSelector from './GuestCountSelector/GuestCountSelector.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [adultCount, setAdultCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [introVideoPoster, setIntroVideoPoster] = useState(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    programListings,
    fetchProgramListingsInProgress,
    fetchProgramListingsError,
    onFetchProgramListings,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const publicDataForHook = currentListing.attributes?.publicData || {};
  const listingTypeForHook = publicDataForHook.listingType;
  const currentListingIdUuid = currentListing.id?.uuid;
  const introVideoUrlForHook = publicDataForHook.introVideo?.url;
  const introVideoThumbnailUrlForHook = publicDataForHook.introVideo?.thumbnailUrl;

  // Fetch program listings (oneOnOne & workshop) when listing type is 'program'
  // NOTE: This hook must be before any early returns to satisfy React's rules of hooks.
  useEffect(() => {
    if (listingTypeForHook === 'program' && currentListingIdUuid) {
      onFetchProgramListings(currentListingIdUuid);
    }
  }, [listingTypeForHook, currentListingIdUuid]);

  // iOS Safari doesn't paint a thumbnail frame into <video> without an explicit
  // poster image (unlike other browsers, which render one from preload metadata).
  // New uploads store a real thumbnail at upload time (see EditListingPhotosForm),
  // so this client-side capture only runs as a fallback for older listings that
  // were saved before that existed. NOTE: hook must run before any early returns
  // to satisfy React's rules of hooks.
  useEffect(() => {
    if (introVideoThumbnailUrlForHook || !introVideoUrlForHook || typeof document === 'undefined') {
      return undefined;
    }
    let cancelled = false;
    const videoEl = document.createElement('video');
    videoEl.crossOrigin = 'anonymous';
    videoEl.preload = 'auto';
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.src = introVideoUrlForHook;

    const captureFrame = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        if (!canvas.width || !canvas.height) {
          return;
        }
        canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        if (!cancelled) {
          setIntroVideoPoster(dataUrl);
        }
      } catch (e) {
        // Cross-origin video served without CORS headers taints the canvas;
        // fall back silently to no poster (same as previous behavior).
      }
    };
    const handleLoadedData = () => {
      if (videoEl.currentTime === 0) {
        videoEl.currentTime = Math.min(0.1, videoEl.duration || 0.1);
      } else {
        captureFrame();
      }
    };
    const handleSeeked = () => captureFrame();

    videoEl.addEventListener('loadeddata', handleLoadedData);
    videoEl.addEventListener('seeked', handleSeeked);

    return () => {
      cancelled = true;
      videoEl.removeEventListener('loadeddata', handleLoadedData);
      videoEl.removeEventListener('seeked', handleSeeked);
    };
  }, [introVideoUrlForHook, introVideoThumbnailUrlForHook]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return (
      <ListingPageCarouselSkeleton topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const {
    category,
    listingType,
    transactionProcessAlias,
    unitType,
    intentionOrBenefits = '',
    introVideo = {},
    introAudio = {},
    programOutline = {},
    tags,
    skillLevel = '',
    theme = '',
    languagePreference = null,
    amenities = [],
    checkInTime,
    checkOutTime,
    maxGuest,
    uniqueFeatures,
    houseRules,
  } = publicData;

  const skillLevelMaybe = skillLevel ? (
    <span className={css.skillLabel}>
      {skillLevel === 'beginner'
        ? intl.formatMessage({ id: 'ListingPage.beginnerLabel' })
        : skillLevel === 'intermediate'
        ? intl.formatMessage({ id: 'ListingPage.intermediateLabel' })
        : skillLevel === 'advanced'
        ? intl.formatMessage({ id: 'ListingPage.advancedLabel' })
        : skillLevel}
    </span>
  ) : null;

  const themeMaybe = theme ? (
    <span className={css.skillLabel}>
      {theme === 'healing'
        ? intl.formatMessage({ id: 'ListingPage.healingLabel' })
        : theme === 'clarity'
        ? intl.formatMessage({ id: 'ListingPage.clarityLabel' })
        : theme === 'grounding'
        ? intl.formatMessage({ id: 'ListingPage.groundingLabel' })
        : theme === 'manifestation'
        ? intl.formatMessage({ id: 'ListingPage.manifestationLabel' })
        : theme}
    </span>
  ) : null;

  const languageMaybe = languagePreference ? (
    <span className={css.skillLabel}>
      {languagePreference.en === 'English'
        ? intl.formatMessage({ id: 'ListingPage.englishLabel' })
        : languagePreference.en === 'Thai'
        ? intl.formatMessage({ id: 'ListingPage.thaiLanguageLabel' })
        : null}
    </span>
  ) : null;

  const normalizedIntentionOrBenefits = Array.isArray(intentionOrBenefits)
    ? intentionOrBenefits.filter(point => typeof point === 'string' && point.trim().length > 0)
    : typeof intentionOrBenefits === 'string' && intentionOrBenefits.trim().length > 0
    ? [intentionOrBenefits]
    : [];

  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (authorNeedsPayoutDetails && !isPayoutMethodConnected(currentUser));
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  // Guest count — for accommodation and spacesOrVenues listings
  const isGuestCountListing = listingType === 'accommodation' || listingType === 'spacesOrVenues';
  const maxGuestRaw = maxGuest;

  const handleAdultIncrement = () => {
    const max = parseInt(maxGuestRaw, 10) || 1;
    setAdultCount(prev => (prev + childrenCount < max ? prev + 1 : prev));
  };
  const handleAdultDecrement = () => {
    setAdultCount(prev => Math.max(prev - 1, 1));
  };
  const handleChildrenIncrement = () => {
    const max = parseInt(maxGuestRaw, 10) || 1;
    setChildrenCount(prev => (adultCount + prev < max ? prev + 1 : prev));
  };
  const handleChildrenDecrement = () => {
    setChildrenCount(prev => Math.max(prev - 1, 0));
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(isGuestCountListing ? { ...values, adultCount, childrenCount } : values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );

  const instructions = (
    <>
      <button className={css.instructionsBtn}>
        {intl.formatMessage({ id: 'ListingPage.instantDownloadBtn' })}
      </button>
    </>
  );

  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            {showListingImage && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )}
            <div
              className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
            >
              {showListingImage ? (
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div>
            {/* <SectionTextMaybe text={description} showAsIngress /> */}
            {description && (
              <>
                <span className={css.serviceHeading}>
                  {listingType === 'physicalProduct' || listingType === 'digitalProduct' ? (
                    <>{intl.formatMessage({ id: 'ListingPage.productDescHeading' })}</>
                  ) : (
                    <>{intl.formatMessage({ id: 'ListingPage.serviceDescHeading' })}</>
                  )}
                </span>
                <ExpandableBio className={css.ingress} bio={description} />
              </>
            )}

            {normalizedIntentionOrBenefits.length > 0 && (
              <>
                <span className={css.serviceHeading}>
                  {listingType === 'physicalProduct' || listingType === 'digitalProduct' ? (
                    <>{intl.formatMessage({ id: 'ListingPage.productBenefitHeading' })}</>
                  ) : (
                    <>{intl.formatMessage({ id: 'ListingPage.benefitHeading' })}</>
                  )}
                </span>
                <div className={css.benefitList}>
                  {normalizedIntentionOrBenefits.map((point, index) => (
                    <div key={`${point}-${index}`} className={css.benefit}>
                      <BenefitIcon className={css.icon} />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {programListings &&
              programListings.length > 0 &&
              (() => {
                const oneOnOneListings = programListings.filter(item => item.type === 'oneOnOne');
                const workshopListings = programListings.filter(item => item.type === 'workshop');
                const sortedListings = [...oneOnOneListings, ...workshopListings];
                return (
                  <>
                    <span className={css.serviceHeading}>
                      {intl.formatMessage({ id: 'ListingPage.bundleSessionHeading' })}
                    </span>
                    <p className={css.bundleSessionSubtext}>
                      {intl.formatMessage(
                        { id: 'ListingPage.bundleSessionSubtext' },
                        {
                          br: chunks => <span className={css.highlight}>{chunks}</span>,
                        }
                      )}
                    </p>
                    <div className={css.programListingsContainer}>
                      {sortedListings.map(item => {
                        const typeBadgeLabel =
                          item.type === 'oneOnOne'
                            ? intl.formatMessage({ id: 'ListingPage.oneOnOneLabel' })
                            : intl.formatMessage({ id: 'ListingPage.workshopLabel' });
                        const isExisting = item.subCategory_id != null;
                        const statusLabel = isExisting
                          ? intl.formatMessage({ id: 'ListingPage.bundleSessionExisting' })
                          : intl.formatMessage({ id: 'ListingPage.bundleSessionNew' });
                        return (
                          <div key={item._id} className={css.programListingRow}>
                            <div className={css.programListingLeft}>
                              {item.data?.image_url ? (
                                <img
                                  src={item.data.image_url}
                                  alt="icon"
                                  className={css.programListingAvatar}
                                />
                              ) : (
                                <div className={css.programListingAvatarPlaceholder}>
                                  {item.type === 'oneOnOne' ? <OneOnOneIcon /> : <WorkshopIcon />}
                                </div>
                              )}
                              <div className={css.programListingInfo}>
                                <h4 className={css.programListingTitle}>
                                  {item.data?.title || typeBadgeLabel}
                                </h4>
                                <div className={css.mobileLabel}>
                                  <span className={css.programListingTypeBadge}>
                                    {typeBadgeLabel}
                                  </span>
                                  <span
                                    className={
                                      isExisting
                                        ? css.programListingStatusExisting
                                        : css.programListingStatusNew
                                    }
                                  >
                                    {statusLabel}
                                  </span>
                                </div>
                                {item.data?.description && (
                                  <p className={css.programListingDescription}>
                                    {item.data.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className={css.programListingRight}>
                              <span className={css.programListingTypeBadge}>{typeBadgeLabel}</span>
                              <span
                                className={
                                  isExisting
                                    ? css.programListingStatusExisting
                                    : css.programListingStatusNew
                                }
                              >
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

            {tags && tags.length > 0 && (
              <>
                <span className={css.serviceHeading}>
                  {intl.formatMessage({ id: 'ListingPage.tagsLabel' })}
                </span>
                <div className={css.tagsContainer}>
                  {tags.map(tag => (
                    <span key={tag} className={css.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}

            {category === 'host' && (amenities?.length > 0 || checkInTime || checkOutTime) ? (
              <>
                <div className={css.accommodationDetails}>
                  {amenities && amenities.length > 0 && (
                    <div className={css.amenitiesSection}>
                      <h5 className={css.serviceHeading}>
                        {intl.formatMessage({ id: 'ListingPage.amenitiesLabel' })}
                      </h5>
                      <ul className={css.amenitiesList}>
                        {amenities.map((amenity, index) => (
                          <li key={`${amenity}-${index}`} className={css.amenityItem}>
                            {amenity === 'wifi'
                              ? intl.formatMessage({ id: 'ListingPage.amenityWifi' })
                              : amenity === 'airConditioning'
                              ? intl.formatMessage({ id: 'ListingPage.amenityAirConditioning' })
                              : amenity === 'kitchenAccess'
                              ? intl.formatMessage({ id: 'ListingPage.amenityKitchen' })
                              : amenity === 'parking'
                              ? intl.formatMessage({ id: 'ListingPage.amenityParking' })
                              : amenity === 'tvDisplay'
                              ? intl.formatMessage({ id: 'ListingPage.amenityTvDisplay' })
                              : amenity === 'soundSystem'
                              ? intl.formatMessage({ id: 'ListingPage.amenitySoundSystem' })
                              : amenity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(checkInTime || checkOutTime) && (
                    <div className={css.checkTimesSection}>
                      {checkInTime && (
                        <div className={css.checkTimeItem}>
                          <span className={css.timeLabel}>
                            {intl.formatMessage({ id: 'ListingPage.checkInTimeLabel' })}{' '}
                          </span>
                          <span className={css.timeValue}>{checkInTime}</span>
                        </div>
                      )}
                      {checkOutTime && (
                        <div className={css.checkTimeItem}>
                          <span className={css.timeLabel}>
                            {intl.formatMessage({ id: 'ListingPage.checkOutTimeLabel' })}{' '}
                          </span>
                          <span className={css.timeValue}>{checkOutTime}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}

            {category === 'host' && uniqueFeatures?.trim() && (
              <>
                <span className={css.serviceHeading}>
                  {intl.formatMessage({ id: 'ListingPage.uniqueFeaturesLabel' })}
                </span>
                <ExpandableBio className={css.ingress} bio={uniqueFeatures} />
              </>
            )}

            {category === 'host' && houseRules?.trim() && (
              <>
                <span className={css.serviceHeading}>
                  {intl.formatMessage({ id: 'ListingPage.houseRulesLabel' })}
                </span>
                <ExpandableBio className={css.ingress} bio={houseRules} />
              </>
            )}

            {skillLevel || theme ? (
              <div className={css.additionalInfoContainer}>
                {skillLevel && (
                  <span className={css.additionalInfoLabel}>
                    {intl.formatMessage({ id: 'ListingPage.skillLevelLabel' }, { skillLevelMaybe })}
                  </span>
                )}
                {theme && (
                  <span className={css.additionalInfoLabel}>
                    {intl.formatMessage({ id: 'ListingPage.themeLabel' }, { themeMaybe })}
                  </span>
                )}
              </div>
            ) : null}

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              desktopBio
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            {listingType === 'digitalProduct' && (
              <div className={css.digitalProductSection}>
                <span className={css.serviceHeading}>
                  {intl.formatMessage({ id: 'ListingPage.instantDownload' })}
                </span>
                <p className={css.digitalProductSubtext}>
                  {intl.formatMessage(
                    { id: 'ListingPage.instantDownloadSubText1' },
                    { instructions }
                  )}
                </p>
                <p className={css.digitalProductSubtext}>
                  {intl.formatMessage({ id: 'ListingPage.instantDownloadSubText2' })}
                </p>
              </div>
            )}

            <SectionReviews
              reviews={reviews}
              fetchReviewsError={fetchReviewsError}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            <div className={css.desktopAuthorSection}>
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
          </div>
          <div className={css.orderColumnForProductLayout}>
            <div className={css.orderPanelWrapper}>
              <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              description={description}
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              guestCountSelector={
                isGuestCountListing ? (
                  <GuestCountSelector
                    adultCount={adultCount}
                    childrenCount={childrenCount}
                    onIncrementAdult={handleAdultIncrement}
                    onDecrementAdult={handleAdultDecrement}
                    onIncrementChildren={handleChildrenIncrement}
                    onDecrementChildren={handleChildrenDecrement}
                    maxGuest={maxGuestRaw}
                    intl={intl}
                  />
                ) : null
              }
              />
            </div>

            <div>
            {introVideo?.url || introAudio?.url || programOutline?.url ? (
              <div className={css.mediaDiv}>
                <span className={css.serviceHeading}>
                  {intl.formatMessage({ id: 'ListingPage.mediaResourcesHeading' })}
                </span>

                {introVideo?.url ? (
                  <div className={css.mediaSection}>
                    <PipAwareVideo
                      className={css.videoCard}
                      videoClassName={css.video}
                      controls
                      playsInline
                      preload="auto"
                      poster={introVideo.thumbnailUrl || introVideoPoster || undefined}
                    >
                      <source src={introVideo.url} type="video/mp4" />
                    </PipAwareVideo>
                  </div>
                ) : null}

                {introAudio?.url ? (
                  <>
                    <p className={css.mediaLabel}>
                      {intl.formatMessage(
                        { id: 'ListingPage.audioMessageLabel' },
                        { author: authorDisplayName }
                      )}
                    </p>
                    <div className={css.audioSection}>
                      <audio className={css.audioPlayer} controls preload="metadata">
                        <source src={introAudio.url} type="audio/mpeg" />
                        <source src={introAudio.url} type="audio/mp4" />
                      </audio>
                    </div>
                  </>
                ) : null}

                {programOutline?.url ? (
                  <>
                    <p className={css.mediaLabel}>
                      {intl.formatMessage({ id: 'ListingPage.programOutlineLabel' })}
                    </p>
                    <div className={css.pdfSection}>
                      <div className={css.fileCardLeft}>
                        <PdfIcon />
                        <span className={css.fileCardName}>{'File.pdf'}</span>
                      </div>
                      <a
                        href={programOutline.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        aria-label="Download program outline"
                      >
                        <DownloadIcon />
                      </a>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            {languagePreference ? (
              <div className={css.languageContainer}>
                <CiGlobe />
                <span className={css.languageLabel}>
                  {intl.formatMessage({ id: 'ListingPage.languageLabel' }, { languageMaybe })}
                </span>
              </div>
            ) : null}

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />

            <div className={css.mobileAuthorSection}>
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
                showInquiryModal={false}
              />
            </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    programListings,
    fetchProgramListingsInProgress,
    fetchProgramListingsError,
    hasActiveCircleSubscription,
    circleSubscriptionTransactionId,
    fetchCircleSubscriptionStatusInProgress,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    programListings,
    fetchProgramListingsInProgress,
    fetchProgramListingsError,
    hasActiveCircleSubscription,
    circleSubscriptionTransactionId,
    fetchCircleSubscriptionStatusInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onFetchProgramListings: listingId => dispatch(fetchProgramListings(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
